import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { matchesTypedConfirmation } from "@/libs/typed-confirmation";

type ModalConfirmOptions = {
  title: string;
  description: string;
  onConfirm: () => void | Promise<void>;
  confirmText?: string;
  cancelText?: string;
  /**
   * When set, the Confirm button stays disabled until the user types this
   * exact value into an inline text field (case-sensitive, FE-15/D53). Use
   * this in place of a second stacked confirmation dialog for destructive
   * actions that warrant one deliberate typed act rather than two reflexive
   * clicks — e.g. typing a tag's name before deleting it.
   *
   * Optional and additive: every existing caller that omits it keeps the
   * original single-step confirm behavior unchanged.
   */
  confirmationText?: string;
  /**
   * Label shown above the typed-confirmation input. Defaults to a generic
   * prompt referencing `confirmationText`. Only used when `confirmationText`
   * is set.
   */
  confirmationLabel?: string;
  /**
   * Visual style of the Confirm button. Defaults to `"default"` (unchanged
   * from every existing caller's behavior); pass `"destructive"` for actions
   * that warrant destructive styling on the confirm action itself, not just
   * on the trigger that opened the dialog.
   */
  confirmVariant?: "default" | "destructive";
};

type ModalConfirmAPI = {
  show: (options: ModalConfirmOptions) => void;
};

type ModalConfirmProviderProps = {
  children: ReactNode;
};

type DialogState = {
  isOpen: boolean;
  isProcessing: boolean;
  options: ModalConfirmOptions;
  /** Current value of the typed-confirmation input, when `confirmationText` is set. */
  typedValue: string;
  /**
   * The element that had focus at the moment `ModalConfirm.show()` was
   * called (the trigger button, since a click handler runs synchronously
   * before this state is set). Because this dialog is a single permanently
   * mounted singleton rather than a co-located `<AlertDialogTrigger>`,
   * Radix's own implicit focus-return bookkeeping doesn't apply — we
   * restore focus to this element ourselves in `onCloseAutoFocus` (DEF-12).
   */
  triggerElement: Element | null;
};

const DEFAULT_OPTIONS: ModalConfirmOptions = {
  title: "",
  description: "",
  onConfirm: () => {},
  confirmText: "Delete",
  cancelText: "Cancel",
};

/**
 * Refocuses `element` if it's still an attachable, focusable node in the
 * document (guards against the page having navigated away, or the trigger
 * having otherwise been removed, while the dialog was open — refocusing a
 * detached element throws in some engines and silently no-ops in others,
 * neither of which should crash the close path).
 */
function restoreFocus(element: Element | null) {
  if (!element || !(element instanceof HTMLElement)) return;
  if (!document.contains(element)) return;
  try {
    element.focus();
  } catch {
    // Best-effort restore only — never let a failed refocus break dialog close.
  }
}

// Singleton ref untuk imperative API
const ModalConfirmRef: { current: ModalConfirmAPI | null } = { current: null };

export const ModalConfirm: ModalConfirmAPI = {
  show: (options: ModalConfirmOptions) => {
    if (!ModalConfirmRef.current) {
      throw new Error(
        "ModalConfirm not initialized. Make sure ModalConfirmProvider is mounted in your app."
      );
    }
    ModalConfirmRef.current.show(options);
  },
};

export function ModalConfirmProvider({ children }: ModalConfirmProviderProps) {
  const [state, setState] = useState<DialogState>({
    isOpen: false,
    isProcessing: false,
    options: DEFAULT_OPTIONS,
    typedValue: "",
    triggerElement: null,
  });
  const confirmationInputId = useId();

  const apiRef = useRef<ModalConfirmAPI>({
    show: (newOptions: ModalConfirmOptions) => {
      // Snapshot synchronously: a caller's onClick runs before this state
      // update, so document.activeElement here is still the trigger button
      // (DEF-12).
      const triggerElement = document.activeElement;
      setState({
        isOpen: true,
        isProcessing: false,
        options: {
          confirmText: "Delete",
          cancelText: "Cancel",
          ...newOptions,
        },
        typedValue: "",
        triggerElement,
      });
    },
  });

  // Register API ref on mount
  useEffect(() => {
    ModalConfirmRef.current = apiRef.current;
  }, []);

  const handleClose = () => {
    if (state.isProcessing) return;
    setState((prev) => ({
      ...prev,
      isOpen: false,
      isProcessing: false,
    }));
  };

  const handleConfirm = async () => {
    setState((prev) => ({ ...prev, isProcessing: true }));
    try {
      await state.options.onConfirm();
      setState((prev) => ({
        ...prev,
        isOpen: false,
        isProcessing: false,
      }));
    } catch (error) {
      console.error("Confirm action failed:", error);
      setState((prev) => ({ ...prev, isProcessing: false }));
    }
  };

  const { confirmationText, confirmationLabel } = state.options;
  const requiresTypedConfirmation = !!confirmationText;
  const isConfirmationSatisfied =
    !requiresTypedConfirmation || matchesTypedConfirmation(state.typedValue, confirmationText);
  const isConfirmDisabled = state.isProcessing || !isConfirmationSatisfied;

  return (
    <>
      {children}
      <AlertDialog onOpenChange={handleClose} open={state.isOpen}>
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            // Radix's own "restore focus to the previously focused element"
            // default doesn't produce the right result through this
            // singleton indirection (DEF-12) — restore to the actual
            // trigger we captured at show()-time instead.
            event.preventDefault();
            restoreFocus(state.triggerElement);
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{state.options.title}</AlertDialogTitle>
            <AlertDialogDescription>{state.options.description}</AlertDialogDescription>
          </AlertDialogHeader>
          {requiresTypedConfirmation && (
            <div className="flex flex-col gap-2">
              <Label htmlFor={confirmationInputId}>
                {confirmationLabel ?? (
                  <>
                    Type <span className="font-semibold">{confirmationText}</span> to confirm.
                  </>
                )}
              </Label>
              <Input
                autoComplete="off"
                disabled={state.isProcessing}
                id={confirmationInputId}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, typedValue: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isConfirmationSatisfied && !state.isProcessing) {
                    e.preventDefault();
                    void handleConfirm();
                  }
                }}
                value={state.typedValue}
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={state.isProcessing}>
              {state.options.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: state.options.confirmVariant ?? "default" })}
              disabled={isConfirmDisabled}
              onClick={() => void handleConfirm()}
            >
              {state.isProcessing ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Processing...
                </>
              ) : (
                state.options.confirmText
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export type { ModalConfirmOptions };
